<template>
  <div>
    <div style="position: relative;">
      <div class="RenderIndex" id="webgl">
        <canvas ref="canvas"></canvas>
      </div>
      <div id="directionPointer" class="directionPointer" style="display: block;"></div>
    </div>
  </div>
</template>
<script>
import { TrackballControls } from 'three/addons/controls/TrackballControls.js'
import * as THREE from 'three'
import Bus from '../../utils/bus.js'
import { mapState, mapMutations } from 'vuex'
import { directionPointerInit, resetPointer, renderPointer, getPointerData } from './Pointer'

const MAX_PIXEL_RATIO = 2
const SHADOW_MAP_SIZE = 1024

export default {
  data() {
    return {
      autoRotate: false,
      isUserInteracting: false,
      mouseDownX: null,
      mouseUpX: null,
      mouseDownY: null,
      mouseUpY: null,
      cameraPosition: null,
      camera: null,
      orbitControl: null,
      modelGroup: null,
      renderer: null,
      scene: null,
      touchStart1X: null,
      touchStart1Y: null,
      touchStart2X: null,
      touchStart2Y: null,
      animationId: null,
    }
  },
  computed: {
    ...mapState(['measurementVisible', 'modelReset']),
    needsRender() {
      return this.autoRotate || this.isUserInteracting
    },
  },
  async mounted() {
    const raycaster = new THREE.Raycaster()
    this.scene = new THREE.Scene()
    this.camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.01, 1000)
    const point = new THREE.Vector2()
    const innerW = window.innerWidth
    const innerH = window.innerHeight
    this.cameraPosition = innerW >= 768 && innerH >= 900 ? -440 : -430

    await this.initScene(raycaster, point)

    const peopleModel = this.$store.state.peopleModel
    await directionPointerInit(peopleModel)

    this.setupTouchHandlers()
    this.setupInteractionTracking()
    Bus.$on('val', (data) => { this.autoRotate = data })
    this.startRenderLoop()
  },
  beforeDestroy() {
    Bus.$off('val')
    if (this.animationId) cancelAnimationFrame(this.animationId)
    if (this.renderer) {
      this.renderer.dispose()
      this.renderer.forceContextLoss()
    }
  },
  methods: {
    ...mapMutations(['setOrbitControl', 'setRendererRef', 'setModelReset']),

    centerModels(parentObject) {
      parentObject.updateMatrixWorld()
      const group = new THREE.Group()
      const box = new THREE.Box3().setFromObject(parentObject)
      const center = new THREE.Vector3()
      box.getCenter(center)
      parentObject.position.sub(center)
      group.add(parentObject)
      group.position.copy(center.add(parentObject.position))
      this.modelGroup = group
    },

    setupInteractionTracking() {
      const el = document.getElementById('webgl')
      el.addEventListener('pointerdown', () => { this.isUserInteracting = true })
      el.addEventListener('pointerup', () => { this.isUserInteracting = false })
      el.addEventListener('wheel', () => { this.isUserInteracting = true })
    },

    setupTouchHandlers() {
      window.addEventListener('touchstart', (event) => {
        if (event.touches.length === 2) {
          this.touchStart1X = event.touches[0].clientX
          this.touchStart1Y = event.touches[0].clientY
          this.touchStart2X = event.touches[1].clientX
          this.touchStart2Y = event.touches[1].clientY
        }
      })
      window.addEventListener('touchmove', (event) => {
        if (event.touches.length !== 2) return
        event.preventDefault()
        const v1 = new THREE.Vector3(
          this.touchStart2X - this.touchStart1X,
          this.touchStart2Y - this.touchStart1Y, 0
        )
        const c1x = event.touches[0].clientX
        const c1y = event.touches[0].clientY
        const c2x = event.touches[1].clientX
        const c2y = event.touches[1].clientY
        const v2 = new THREE.Vector3(c2x - c1x, c2y - c1y, 0)
        const angle = v2.angleTo(v1)
        v1.cross(v2)
        this.touchStart1X = c1x; this.touchStart1Y = c1y
        this.touchStart2X = c2x; this.touchStart2Y = c2y
        this.rotateBothModels(angle * (v1.z > 0 ? 1 : -1))
      })
    },

    rotateBothModels(angle) {
      const axis = new THREE.Vector3().setFromMatrixColumn(this.camera.matrixWorld, 2)
      const ptr = getPointerData()
      const localAxis = new THREE.Vector3().setFromMatrixColumn(ptr.camera.matrixWorld, 2).normalize()
      this.modelGroup.rotateOnWorldAxis(axis.normalize(), -angle * 1.4)
      if (ptr.modelMesh && ptr.modelMesh.parent) {
        ptr.modelMesh.parent.rotateOnWorldAxis(localAxis, -angle * 1.4)
      }
    },

    render() {
      this.renderer.render(this.scene, this.camera)
      renderPointer()
    },

    startRenderLoop() {
      const loop = () => {
        this.animationId = requestAnimationFrame(loop)
        if (this.autoRotate) this.doAutoRotate()
        this.orbitControl.update()
        this.render()
      }
      loop()
    },

    doAutoRotate() {
      const dir = this.camera.getWorldDirection(new THREE.Vector3()).normalize()
      const axis = new THREE.Vector3(
        dir.x + 2 * this.camera.up.x,
        dir.y + 2 * this.camera.up.y,
        dir.z + 2 * this.camera.up.z
      ).normalize()
      this.camera.position.applyAxisAngle(axis, (2 * Math.PI) / 3600 * 6)
    },

    async initScene(raycaster, point) {
      if (!this.$store.state.listColor) return

      this.modelGroup = this.$store.state.materials
      this.centerModels(this.modelGroup)
      this.scene.updateMatrixWorld(true)

      const container = document.getElementById('webgl')
      this.camera.position.y = this.cameraPosition
      this.camera.up.set(0, 0, 1)

      this.renderer = new THREE.WebGLRenderer({
        canvas: this.$refs.canvas,
        alpha: true,
        antialias: true,
      })
      this.scene.add(this.camera)

      this.scene.add(new THREE.AmbientLight(0xffffff, 0.15))
      const dirLight = new THREE.DirectionalLight(0xffffff, 0.7)
      dirLight.castShadow = true
      dirLight.shadow.mapSize.set(SHADOW_MAP_SIZE, SHADOW_MAP_SIZE)
      this.camera.add(dirLight)

      this.renderer.setSize(window.innerWidth, window.innerHeight)
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO))
      this.renderer.shadowMap.enabled = true
      this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
      container.appendChild(this.renderer.domElement)
      this.setRendererRef([this.renderer, this.camera, container])

      this.scene.add(this.modelGroup)

      this.orbitControl = new TrackballControls(this.camera, container)
      this.orbitControl.target.set(0, 0, 0)
      this.orbitControl.minDistance = 10
      this.orbitControl.maxDistance = 900
      this.orbitControl.zoomSpeed = 2
      this.orbitControl.panSpeed = 2
      this.orbitControl.dynamicDampingFactor = 0.3
      this.orbitControl.rotateSpeed = 4
      this.orbitControl.staticMoving = true
      this.orbitControl.enableDamping = true
      this.orbitControl.addEventListener('end', () => {
        if (this.autoRotate) {
          this.autoRotate = false
          Bus.$emit('vals', false)
        }
      })
      this.setOrbitControl(this.orbitControl)

      raycaster.firstHitOnly = true
      this.setupClickHandler(raycaster, point)
    },

    setupClickHandler(raycaster, point) {
      const el = document.getElementById('webgl')
      el.addEventListener('mousedown', (e) => {
        this.mouseDownX = e.clientX
        this.mouseDownY = e.clientY
      })
      el.addEventListener('click', (e) => {
        this.mouseUpX = e.clientX
        this.mouseUpY = e.clientY
        if (this.mouseDownX !== this.mouseUpX || this.mouseDownY !== this.mouseUpY) return

        const bounds = this.renderer.domElement.getBoundingClientRect()
        point.x = (e.offsetX / bounds.width) * 2 - 1
        point.y = -(e.offsetY / bounds.height) * 2 + 1
        raycaster.setFromCamera(point, this.camera)

        const targets = this.$store.state.boundingBox
          .filter(item => item.mesh.visible)
          .map(item => item.mesh)

        const hit = raycaster.intersectObjects(targets)
        if (hit.length > 0) this.$emit('modelClick', hit[0].object)
      })
    },
  },
  watch: {
    measurementVisible(val) { /* handled by child components */ },
    modelReset(val) {
      if (val) {
        resetPointer()
        this.orbitControl.reset()
        this.camera.up.set(0, 0, 1)
        this.camera.lookAt(0, 0, 0)
        this.modelGroup.rotation.set(0, 0, 0)
        this.setModelReset(false)
      }
    },
  },
}
</script>
<style lang="scss" scoped>
.RenderIndex { width: 100vw; }
.directionPointer {
  width: 100px; height: 110px;
  position: absolute; right: -10px; top: 40px;
}
</style>
